import type { ConversationAgentSummary } from "~/services/agents";
import { AssistantTurn } from "./AssistantTurn";
import { UserMessage } from "./UserMessage";
import type { Turn } from "./useTurns";

interface ConversationTurnsProps {
  turns: Turn[];
  // Agent identity for assistant avatars and turn labels; null for legacy
  // agentless conversations. Narrowed to the label + avatar subset so read-only
  // views can supply it without the agent's full config or author metadata.
  conversationAgent: ConversationAgentSummary | null;
  conversationId: string;
  // Live-only: surfaces the "Stopping" label on the in-flight turn.
  isCancelling?: boolean;
  // Read-only feedback rendering (admin review).
  readOnlyFeedback?: boolean;
  // Resume a paused human-in-the-loop tool call. Omitted in read-only views,
  // where the cards render but cannot be answered.
  onAnswer?: (toolCallId: string, answer: string) => void | Promise<void>;
  // Tool-call ids genuinely paused awaiting human input; gates answerability
  // of the HITL and fallback cards.
  awaitingToolCallIds?: ReadonlySet<string>;
}

// The single user/assistant turn dispatch shared by the live `Chat` and the
// read-only (shared / admin) views. Keeping it in one place means new turn
// kinds and per-turn rendering rules reach every surface automatically rather
// than having to be mirrored into each caller. Callers own their own scroll
// container and `<ol>` wrapper.
export function ConversationTurns({
  turns,
  conversationAgent,
  conversationId,
  isCancelling,
  readOnlyFeedback,
  onAnswer,
  awaitingToolCallIds,
}: ConversationTurnsProps) {
  return (
    <>
      {turns.map((turn) =>
        turn.kind === "user" ? (
          <UserMessage key={turn.key} text={turn.text} />
        ) : (
          <AssistantTurn
            key={turn.key}
            turn={turn}
            agent={conversationAgent}
            conversationId={conversationId}
            isCancelling={isCancelling}
            readOnlyFeedback={readOnlyFeedback}
            onAnswer={onAnswer}
            awaitingToolCallIds={awaitingToolCallIds}
          />
        )
      )}
    </>
  );
}
